export const DEFAULT_BRIEF_SEQUENCE_VERSION = "2026-07-global-info-v2";

export function isRetiredBriefSectionId(value: unknown): boolean {
  return ["productivity", "finance"].includes(String(value || "").trim().toLowerCase());
}

export const DEFAULT_BRIEF_SEQUENCE_FALLBACK = [
  "news",
  "market_pulse",
  "watchlist",
  "sport_football",
  "world_affairs",
  "quote",
] as const;

const LAUNCH_COUNTRIES = ["US", "CA", "AU", "GB", "DE", "FR", "IN", "JP"] as const;

type LaunchCountry = (typeof LAUNCH_COUNTRIES)[number];

const PRIMARY_SPORT_BY_COUNTRY: Record<LaunchCountry, string> = {
  US: "nfl",
  CA: "hockey",
  AU: "afl",
  GB: "football",
  DE: "football",
  FR: "football",
  IN: "cricket",
  JP: "baseball",
};

const SPORT_LABELS: Record<string, string> = {
  afl: "AFL",
  baseball: "Baseball",
  cricket: "Cricket",
  football: "Soccer",
  hockey: "NHL",
  mlb: "MLB",
  nba: "NBA",
  nfl: "NFL",
};

function normalizeCountry(country: string | undefined | null): string {
  return String(country || "US")
    .trim()
    .toUpperCase()
    .slice(0, 2);
}

function isLaunchCountry(country: string): country is LaunchCountry {
  return (LAUNCH_COUNTRIES as readonly string[]).includes(country);
}

function newsSectionId(country: string, category: string): string {
  return `news_global_${country.toLowerCase()}_${category.trim().toLowerCase()}`;
}

function sportSectionId(sportId: string): string {
  return `sport_${sportId.trim().toLowerCase()}`;
}

function primarySport(country: LaunchCountry): string {
  return sportSectionId(PRIMARY_SPORT_BY_COUNTRY[country]);
}

function distinct(values: readonly string[]): string[] {
  const seen = new Set<string>();
  return values.filter((value) => {
    const id = String(value || "").trim();
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function defaultBriefSequenceForCountry(country: string | undefined | null): string[] {
  const cc = normalizeCountry(country);
  if (!isLaunchCountry(cc)) return [...DEFAULT_BRIEF_SEQUENCE_FALLBACK];

  const sequence: Record<LaunchCountry, string[]> = {
    US: [
      "news",
      newsSectionId("US", "world"),
      "market_pulse",
      "watchlist",
      primarySport("US"),
      newsSectionId("US", "ai"),
      "quote",
    ],
    CA: [
      "news",
      newsSectionId("CA", "world"),
      primarySport("CA"),
      "market_pulse",
      "watchlist",
      "quote",
    ],
    AU: [
      "news",
      newsSectionId("AU", "world"),
      primarySport("AU"),
      "cricket",
      "market_pulse",
      "watchlist",
      "quote",
    ],
    GB: [
      "news",
      newsSectionId("GB", "world"),
      primarySport("GB"),
      "market_pulse",
      "watchlist",
      "quote",
    ],
    DE: [
      "news",
      newsSectionId("DE", "world"),
      newsSectionId("DE", "business"),
      "market_pulse",
      "watchlist",
      primarySport("DE"),
      "quote",
    ],
    FR: [
      "news",
      newsSectionId("FR", "world"),
      primarySport("FR"),
      "market_pulse",
      "watchlist",
      "world_affairs",
      "quote",
    ],
    IN: [
      "news",
      newsSectionId("IN", "world"),
      "market_pulse",
      "watchlist",
      "cricket",
      newsSectionId("IN", "technology"),
      "quote",
    ],
    JP: [
      "news",
      "market_pulse",
      "watchlist",
      primarySport("JP"),
      newsSectionId("JP", "technology"),
      "quote",
    ],
  };

  return distinct(sequence[cc]);
}

export function normalizeBriefSectionIds(
  sectionIds: unknown,
  country: string | undefined | null,
): string[] {
  const cleaned = Array.isArray(sectionIds)
    ? distinct(
        sectionIds
          .map((value) =>
            String(value || "")
              .trim()
              .toLowerCase(),
          )
          .filter((value) => value.length > 0 && value.length <= 80 && !isRetiredBriefSectionId(value)),
      )
    : [];
  return cleaned.length ? cleaned : defaultBriefSequenceForCountry(country);
}

export function briefSectionSourceId(sectionId: string): string | null {
  const id = String(sectionId || "")
    .trim()
    .toLowerCase();
  if (!id.startsWith("news_global_")) return null;
  return `category-${id.replace(/^news_/, "").replaceAll("_", "-")}`;
}

function newsCategoryLabel(sectionId: string): string {
  const category = sectionId.split("_").pop() || "news";
  switch (category) {
    case "ai":
      return "AI News";
    case "business":
      return "Biz · Global";
    case "sports":
      return "Sports";
    case "technology":
      return "Tech";
    case "world":
      return "World";
    default:
      return `${category.charAt(0).toUpperCase()}${category.slice(1)}`;
  }
}

export function briefSectionLabel(sectionId: string): string {
  const id = String(sectionId || "")
    .trim()
    .toLowerCase();
  if (id.startsWith("news_global_")) return newsCategoryLabel(id);
  if (id.startsWith("weather_custom_")) return "Weather - Custom City";
  if (id.startsWith("sport_")) {
    const sportId = id.replace(/^sport_/, "");
    return SPORT_LABELS[sportId] || "Sports";
  }
  switch (id) {
    case "spark":
      return "Today's Spark";
    case "news":
      return "News highlights";
    case "market_pulse":
      return "Markets";
    case "watchlist":
      return "Watchlist";
    case "quote":
      return "Quote of the day";
    case "cricket":
      return "Cricket";
    case "world_affairs":
      return "World Facts";
    case "daily_mastery":
      return "Daily Mastery";
    case "today_idea":
      return "Today's Idea";
    case "fun_facts":
      return "Fun facts";
    case "world_facts":
      return "World facts";
    case "space":
    case "space_facts":
      return "Space";
    case "todos":
      return "Today's Tasks";
    case "my_day":
      return "My Day";
    case "weather":
      return "Weather";
    case "commute":
      return "Commute";
    default:
      return "Brief section";
  }
}
